import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';

// Inject model-viewer script once
let modelViewerScriptLoaded = false;
function ensureModelViewerScript() {
  if (modelViewerScriptLoaded) return;
  // If the custom element is already defined, skip loading the script entirely
  if (typeof customElements !== 'undefined' && customElements.get('model-viewer')) {
    modelViewerScriptLoaded = true;
    return;
  }
  if (document.querySelector('script[data-mv]')) {
    modelViewerScriptLoaded = true;
    return;
  }
  const script = document.createElement('script');
  script.type = 'module';
  script?.setAttribute('data-mv', '1');
  script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
  document.head?.appendChild(script);
  modelViewerScriptLoaded = true;
}

// Detect URLs that are web pages (not 3D model files) — e.g. Kiri Engine viewer links
function isWebPageUrl(url) {
  if (!url) return false;
  try {
    const { hostname, pathname } = new URL(url);
    // Kiri Engine and similar web-based AR viewers
    if (hostname?.includes('kiriengine.app')) return true;
    // Any URL that doesn't end with a known 3D model extension is treated as a web page
    const ext = pathname?.split('.')?.pop()?.toLowerCase();
    const modelExts = ['glb', 'gltf', 'usdz', 'obj', 'fbx'];
    if (!modelExts?.includes(ext) && (hostname?.includes('kiri') || hostname?.includes('ar-viewer') || hostname?.includes('sketchfab'))) return true;
    return false;
  } catch {
    return false;
  }
}

const ARCanvas = ({
  modelUrl,
  rotation,
  scale,
  onLoadComplete,
  onLoadError,
  onARSessionChange,
  modelViewerRef,
  dish,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [arSupported, setArSupported] = useState(false);
  const [isARActive, setIsARActive] = useState(false);
  const [urlValidated, setUrlValidated] = useState(false);
  const mvRef = useRef(null);

  // Keep stable refs for callbacks so they never cause effects to re-run
  const onLoadCompleteRef = useRef(onLoadComplete);
  const onLoadErrorRef = useRef(onLoadError);
  useEffect(() => { onLoadCompleteRef.current = onLoadComplete; }, [onLoadComplete]);
  useEffect(() => { onLoadErrorRef.current = onLoadError; }, [onLoadError]);

  const isWebPage = isWebPageUrl(modelUrl);

  useEffect(() => {
    if (!isWebPage) ensureModelViewerScript();
  }, [isWebPage]);

  // Pre-validate URL before passing to model-viewer to avoid JSON parse errors
  useEffect(() => {
    if (!modelUrl || isWebPage) {
      setUrlValidated(true);
      return;
    }

    setUrlValidated(false);
    setError(null);
    setIsLoading(true);

    const validate = async () => {
      try {
        // Use a GET with Range header to fetch just the first 4 bytes
        // This lets us verify the file is actually a GLB/GLTF binary, not an HTML error page
        const res = await fetch(modelUrl, {
          method: 'GET',
          headers: { Range: 'bytes=0-3' },
        });

        const ct = res?.headers?.get('content-type') || '';

        // Block if server returns an HTML page (404 page, redirect, etc.)
        if (ct?.includes('text/html')) {
          const msg = '3D model file not found or URL is invalid. Please re-upload the model.';
          setError(msg);
          setIsLoading(false);
          if (onLoadErrorRef.current) onLoadErrorRef.current(msg);
          return;
        }

        // Block non-ok responses (404, 403, 500, etc.) — 206 Partial Content is also valid
        if (!res?.ok && res?.status !== 206) {
          const msg = `3D model could not be loaded (HTTP ${res?.status}). Please re-upload the model.`;
          setError(msg);
          setIsLoading(false);
          if (onLoadErrorRef.current) onLoadErrorRef.current(msg);
          return;
        }

        // Read the first 4 bytes and check for GLB magic bytes (0x676C5446 = "glTF")
        // or GLTF JSON (starts with '{')
        try {
          const buffer = await res?.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          if (bytes?.length >= 4) {
            const magic = String.fromCharCode(bytes?.[0], bytes?.[1], bytes?.[2], bytes?.[3]);
            const isGlb = magic === 'glTF';
            const isHtmlStart =
              magic?.toLowerCase()?.startsWith('<!do') ||
              magic?.toLowerCase()?.startsWith('<htm') ||
              bytes?.[0] === 0x3c; // '<'

            if (isHtmlStart) {
              const msg = '3D model file not found or URL is invalid. Please re-upload the model.';
              setError(msg);
              setIsLoading(false);
              if (onLoadErrorRef.current) onLoadErrorRef.current(msg);
              return;
            }

            // Check if it looks like a JSON error response (e.g. Supabase {"error":"Not Found"})
            // Valid GLTF JSON must contain "asset" key — a bare JSON error object is not a valid model
            if (bytes?.[0] === 0x7b) { // '{'
              try {
                // Read more bytes to check if it's a JSON error vs real GLTF
                const textDecoder = new TextDecoder();
                const preview = textDecoder?.decode(bytes?.slice(0, Math.min(bytes?.length, 256)));
                const parsed = JSON.parse(preview?.substring(0, preview?.lastIndexOf('}') + 1) || '{}');
                // Real GLTF JSON has an "asset" key; error responses have "error", "message", "statusCode", etc.
                const isGltfJson = parsed?.asset !== undefined;
                const isErrorJson = parsed?.error !== undefined || parsed?.message !== undefined || parsed?.statusCode !== undefined || parsed?.code !== undefined;
                if (!isGltfJson || isErrorJson) {
                  const msg = '3D model file not found or URL is invalid. Please re-upload the model.';
                  setError(msg);
                  setIsLoading(false);
                  if (onLoadErrorRef.current) onLoadErrorRef.current(msg);
                  return;
                }
              } catch {
                // Could not parse JSON preview — treat as invalid
                const msg = '3D model file not found or URL is invalid. Please re-upload the model.';
                setError(msg);
                setIsLoading(false);
                if (onLoadErrorRef.current) onLoadErrorRef.current(msg);
                return;
              }
            }

            if (!isGlb && bytes?.[0] !== 0x7b) {
              // Unknown format — still allow model-viewer to try (might be a valid binary format)
              console.warn('ARCanvas: unexpected file magic bytes, proceeding anyway', magic);
            }
          }
        } catch {
          // Could not read bytes — allow model-viewer to handle it
        }
      } catch {
        // Network/CORS error — allow model-viewer to handle it via its own error event
      }
      setUrlValidated(true);
    };

    validate();
  }, [modelUrl, isWebPage]);

  // Compute and apply real-world scale from dish dimensions after model loads
  const applyDishScale = (mv) => {
    if (!mv || !dish) return;
    const { widthCm, heightCm, depthCm } = dish;
    if (!widthCm && !heightCm && !depthCm) return;

    try {
      const dims = mv?.getDimensions?.();
      if (!dims) return;

      const scaleFactors = [];
      if (widthCm && dims?.x > 0) scaleFactors?.push((widthCm / 100) / dims?.x);
      if (heightCm && dims?.y > 0) scaleFactors?.push((heightCm / 100) / dims?.y);
      if (depthCm && dims?.z > 0) scaleFactors?.push((depthCm / 100) / dims?.z);

      if (scaleFactors?.length === 0) return;

      const uniformScale = scaleFactors?.reduce((a, b) => a + b, 0) / scaleFactors?.length;
      const s = uniformScale?.toFixed(6);
      mv.scale = `${s} ${s} ${s}`;

      // After applying scale, reset camera to fit the model centered on screen
      requestAnimationFrame(() => {
        try {
          mv.cameraOrbit = '0deg 75deg auto';
          mv.cameraTarget = 'auto auto auto';
          mv.fieldOfView = 'auto';
          if (typeof mv?.resetTurntableRotation === 'function') {
            mv?.resetTurntableRotation(0);
          }
        } catch (e) {
          console.warn('ARCanvas: could not reset camera after scale', e);
        }
      });
    } catch (e) {
      console.warn('ARCanvas: could not apply dish scale', e);
    }
  };

  // Attach native DOM event listeners (model-viewer path only)
  useEffect(() => {
    if (isWebPage) return;
    if (!urlValidated) return;
    const mv = mvRef?.current;
    if (!mv || !modelUrl) return;

    const handleLoad = () => {
      setIsLoading(false);
      applyDishScale(mv);
      if (onLoadCompleteRef.current) onLoadCompleteRef.current();
      setTimeout(() => {
        setArSupported(mv?.canActivateAR === true);
      }, 500);
    };

    const handleError = (e) => {
      const sourceMsg = e?.detail?.sourceError?.message || '';
      // model-viewer throws a JSON parse error when it receives an HTML page instead of a GLB/GLTF
      const isJsonParseError = sourceMsg?.toLowerCase()?.includes('json') || sourceMsg?.toLowerCase()?.includes('unexpected token');
      const msg = isJsonParseError
        ? '3D model file not found or URL is invalid. Please re-upload the model.' : (sourceMsg ||'Failed to load 3D model');
      setError(msg);
      setIsLoading(false);
      if (onLoadErrorRef.current) onLoadErrorRef.current(msg);
    };

    const handleARStatus = (e) => {
      const status = e?.detail?.status;
      if (status === 'session-started') {
        setIsARActive(true);
        if (onARSessionChange) onARSessionChange(true);
      }
      if (status === 'not-presenting' || status === 'failed') {
        setIsARActive(false);
        if (onARSessionChange) onARSessionChange(false);
      }
      setArSupported(mv?.canActivateAR === true);
    };

    mv?.addEventListener('load', handleLoad);
    mv?.addEventListener('error', handleError);
    mv?.addEventListener('ar-status', handleARStatus);

    const timer = setTimeout(() => {
      setArSupported(mv?.canActivateAR === true);
    }, 2000);

    return () => {
      mv?.removeEventListener('load', handleLoad);
      mv?.removeEventListener('error', handleError);
      mv?.removeEventListener('ar-status', handleARStatus);
      clearTimeout(timer);
    };
  }, [modelUrl, isWebPage, urlValidated]);

  // Apply rotation & scale via cameraOrbit API (model-viewer path only)
  useEffect(() => {
    if (isWebPage) return;
    const mv = mvRef?.current;
    if (!mv || isARActive) return;
    // Use 'auto' radius so model-viewer always frames the model to fill the viewport
    mv.cameraOrbit = `${rotation}deg 75deg auto`;
    mv.cameraTarget = 'auto auto auto';
    mv.fieldOfView = 'auto';
  }, [rotation, scale, isARActive, isWebPage]);

  const handleActivateAR = () => {
    if (isWebPage) {
      // Open the web-based AR viewer in a new tab
      window.open(modelUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const mv = mvRef?.current;
    if (!mv) return;
    try {
      if (typeof mv?.activateAR === 'function') mv?.activateAR();
    } catch (e) {
      console.warn('ARCanvas: activateAR failed', e);
    }
  };

  if (!modelUrl) {
    return (
      <div className="ar-canvas-error fixed inset-0 flex items-center justify-center bg-background/95 z-ar">
        <div className="text-center px-4 md:px-6 max-w-md">
          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-warning/10 rounded-full flex items-center justify-center">
            <Icon name="Box" size={32} color="var(--color-warning)" />
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
            No AR Model Available
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            No 3D model has been uploaded for this dish yet. Ask the restaurant to upload a .glb file.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ar-canvas-error fixed inset-0 flex items-center justify-center bg-background/95 z-ar">
        <div className="text-center px-4 md:px-6 max-w-md">
          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-destructive/10 rounded-full flex items-center justify-center">
            <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
            AR Loading Failed
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // ── Shared UI overlays (used by both paths) ──
  const sharedOverlays = (
    <>
      {/* "Voir en RA" button */}
      {!isLoading && (
        <div
          style={{
            position: 'fixed',
            bottom: 'clamp(32px, 8vh, 80px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
            width: 'max-content',
          }}
        >
          <button
            onClick={handleActivateAR}
            className="flex items-center gap-2 px-4 py-2.5 sm:px-5 sm:py-3 rounded-full font-body font-semibold text-sm shadow-lg active:scale-95 transition-transform"
            style={{
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(249,115,22,0.45)',
              whiteSpace: 'nowrap',
            }}
          >
            <Icon name="Scan" size={18} color="#fff" />
            Voir en RA
          </button>
          {!arSupported && (
            <p
              className="text-center mt-1 text-xs"
              style={{ color: 'rgba(255,255,255,0.75)', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}
            >
              AR non disponible sur cet appareil
            </p>
          )}
        </div>
      )}

      {isLoading && (
        <div className="ar-loading-overlay fixed inset-0 flex items-center justify-center bg-background/95 z-modal">
          <div className="text-center px-4 md:px-6 max-w-md">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 relative">
              <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
              <div
                className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"
                style={{ animationDuration: '1s' }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon name="Scan" size={24} color="var(--color-primary)" />
              </div>
            </div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-3">
              Chargement du modèle 3D
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Préparation de l'expérience AR…
            </p>
          </div>
        </div>
      )}
    </>
  );

  // ── Web-page AR viewer (Kiri Engine, etc.) — rendered in iframe ──
  if (isWebPage) {
    return (
      <>
        <iframe
          src={modelUrl}
          title="AR Viewer"
          allow="camera *; gyroscope *; accelerometer *; xr-spatial-tracking *; fullscreen *"
          allowFullScreen
          onLoad={() => {
            setIsLoading(false);
            setArSupported(true); // web-based AR viewers handle AR themselves
            if (onLoadComplete) onLoadComplete();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: 40,
            background: 'transparent',
            border: 'none',
          }}
        />
        {sharedOverlays}
      </>
    );
  }

  // ── Standard model-viewer renderer ──
  return (
    <>
      {urlValidated && (
        <model-viewer
          ref={(el) => {
            mvRef.current = el;
            if (modelViewerRef) modelViewerRef.current = el;
          }}
          src={modelUrl}
          ar
          ar-modes="scene-viewer webxr quick-look"
          ar-placement="floor"
          ar-scale="auto"
          camera-controls
          touch-action="pan-y"
          interaction-prompt="none"
          camera-orbit="0deg 75deg auto"
          camera-target="auto auto auto"
          field-of-view="auto"
          min-field-of-view="10deg"
          max-field-of-view="90deg"
          environment-image="neutral"
          exposure="1"
          shadow-intensity="0.8"
          shadow-softness="1"
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: 40,
            background: 'transparent',
            '--poster-color': 'transparent',
          }}
        />
      )}
      {sharedOverlays}
    </>
  );
};

export default ARCanvas;